import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { FullscreenModal } from './fullscreen-modal';

interface SpreadsheetPreviewProps {
  fileUrl: string;
  fileName: string;
}

export const SpreadsheetPreview: React.FC<SpreadsheetPreviewProps> = ({ fileUrl, fileName }) => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const getFileIcon = () => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'xls':
      case 'xlsx':
        return 'mdi:file-excel';
      case 'csv':
        return 'mdi:file-delimited';
      default:
        return 'mdi:file-excel';
    }
  };

  const getFileTypeLabel = () => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'xls':
      case 'xlsx':
        return t('forms.excelSpreadsheet');
      case 'csv':
        return t('forms.csvFile');
      default:
        return t('forms.spreadsheet');
    }
  };

  const handlePreviewClick = () => {
    setShowPreview(!showPreview);
  };

  const handleFullscreenClick = () => {
    setIsFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <Icon icon={getFileIcon()} className="text-4xl text-green-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate" title={fileName}>
            {fileName}
          </p>
          <p className="text-xs text-gray-500">{getFileTypeLabel()}</p>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={handlePreviewClick}
            className="flex-shrink-0 px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200 transition-colors"
          >
            {showPreview ? t('forms.hidePreview') : t('forms.preview')}
          </button>
          <button
            type="button"
            onClick={handleFullscreenClick}
            className="flex-shrink-0 px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-sm hover:bg-purple-200 transition-colors"
            title={t('forms.openInFullscreen')}
          >
            <Icon icon="mdi:fullscreen" className="text-sm" />
          </button>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 px-3 py-1 bg-green-100 text-green-700 rounded-md text-sm hover:bg-green-200 transition-colors"
          >
            {t('forms.download')}
          </a>
        </div>
      </div>
      
      {showPreview && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">{t('forms.filePreview')}</h4>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Icon icon="mdi:information" className="text-sm" />
                <span>{t('forms.previewMayNotShowAllContent')}</span>
              </div>
            </div>
            
            {/* Excel/CSV Preview using Google Sheets viewer or similar */}
            <div className="bg-gray-50 rounded border border-gray-200 p-4 text-center">
              <div className="flex flex-col items-center space-y-3">
                <Icon icon={getFileIcon()} className="text-6xl text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{getFileTypeLabel()}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {fileName.split('.').pop()?.toLowerCase() === 'csv' 
                      ? 'CSV files can be opened in Excel or any spreadsheet application'
                      : 'Excel files contain structured data with multiple sheets and formulas'
                    }
                  </p>
                </div>
                
                {/* Show file information instead of problematic iframe */}
                <div className="w-full max-w-md">
                  <div className="bg-white rounded border border-gray-200 p-6 text-center">
                    <div className="text-gray-400 mb-4">
                      <Icon icon={getFileIcon()} className="text-8xl mx-auto" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-900">{getFileTypeLabel()}</p>
                  <p className="text-xs text-gray-500">
                    {fileName.split('.').pop()?.toLowerCase() === 'csv' 
                      ? t('forms.csvFilesCanBeOpenedInExcel')
                      : t('forms.excelFilesContainStructuredData')
                    }
                  </p>
                      <div className="pt-2">
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
                        >
                          <Icon icon="mdi:download" className="mr-2" />
                          {t('forms.openInExcel')}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Modal */}
      <FullscreenModal
        isOpen={isFullscreen}
        onClose={handleCloseFullscreen}
        title={`${getFileTypeLabel()} - ${fileName}`}
      >
        <div className="h-full flex flex-col">
          <div className="flex-1 bg-gray-50 rounded border border-gray-200 p-8 text-center">
            <div className="flex flex-col items-center justify-center h-full space-y-6">
              <Icon icon={getFileIcon()} className="text-9xl text-green-500" />
              <div className="max-w-2xl">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{getFileTypeLabel()}</h3>
                <p className="text-lg text-gray-600 mb-6">
                  {fileName.split('.').pop()?.toLowerCase() === 'csv' 
                    ? t('forms.csvFilesCanBeOpenedInExcel')
                    : t('forms.excelFilesContainStructuredData')
                  }
                </p>
                <div className="flex justify-center space-x-4">
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg text-lg hover:bg-green-700 transition-colors"
                  >
                    <Icon icon="mdi:download" className="mr-2" />
                    {t('forms.openInExcel')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FullscreenModal>
    </div>
  );
};


